<script setup>
import { useLanguageStore } from "@/stores/languageStore.js";
import { computed, onMounted, ref } from "vue";
import { useRoute, useRouter } from "vue-router";
import { useCustomStarMapStore } from "@/stores/customStarMapStore.js";
import { shareCustomMap } from "@/utils/shareCustomMap.js";
import { localizedCreatorText } from "@/utils/localizedCreatorText.js";

const route = useRoute();
const router = useRouter();
const languageStore = useLanguageStore();
const starMapStore = useCustomStarMapStore();

const selectedLevel = ref(null);
const statusText = ref("");
const errorText = ref("");
const loading = ref(false);

const mapId = computed(() => String(route.params.mapId || ""));
const shareCode = computed(() => String(route.query.shareCode || ""));
const publicAccess = computed(() => String(route.query.access || "") === "public");
const map = computed(() => starMapStore.getMap(mapId.value));
const displayMapName = computed(() => localizedCreatorText(languageStore, map.value?.name, "name"));
const nodes = computed(() => map.value?.nodes || []);
const builtLevels = computed(() => new Set(
  Array.isArray(map.value?.builtLevels) ? map.value.builtLevels.map(Number) : []
));
const isOwner = computed(() => !map.value?.accessMode || map.value.accessMode === "owner");
const builtCount = computed(() => nodes.value.filter(node =>
  Boolean(node.levelData?.rows) || builtLevels.value.has(Number(node.level))
).length);
const litCount = computed(() => nodes.value.filter(node =>
  starMapStore.isLit(node.level, mapId.value)
).length);
const allLit = computed(() => nodes.value.length > 0 && litCount.value === nodes.value.length);
const stageStyle = computed(() => ({ aspectRatio: String(map.value?.aspectRatio || 1) }));
const selectedNode = computed(() => selectedLevel.value
  ? starMapStore.getNode(mapId.value, selectedLevel.value)
  : null);
const playMode = computed(() =>
  String(route.query.mode || "") === "play"
  || String(route.query.from || "") === "workshop-map-select"
  || Boolean(shareCode.value)
  || publicAccess.value
);
const visibilityLabel = computed(() =>
  map.value?.visibility === "public"
    ? languageStore.t("publicMap")
    : map.value?.visibility === "share"
      ? languageStore.t("shareMap")
      : languageStore.t("privateMap")
);

function goParent() {
  const from = String(route.query.from || "");
  if (from === "workshop-map-select") {
    router.push({ name: "workshop-map-select" });
    return;
  }
  if (from === "level-map-selector") {
    router.push({ name: "level-map-selector" });
    return;
  }
  if (from === "map-manager") {
    router.push({ name: "map-manager" });
    return;
  }
  if (from === "star-map-editor") {
    router.push({ name: "star-map-editor", query: { mapId: mapId.value } });
    return;
  }
  if (from === "workshop") {
    router.push({ name: "workshop" });
    return;
  }
  if (from === "complete") {
    router.push({ name: "custom-map-complete", params: { mapId: mapId.value } });
    return;
  }
  if (from === "shared-map" && shareCode.value) {
    router.push({ name: "shared-map", params: { shareCode: shareCode.value } });
    return;
  }
  if (window.history.length > 1) {
    router.back();
    return;
  }
  router.push({ name: "workshop" });
}

function isNodeBuilt(node) {
  return Boolean(node?.levelData?.rows) || builtLevels.value.has(Number(node?.level));
}

function nodeState(node) {
  if (isNodeBuilt(node)) return "lit";
  return "unbuilt";
}

async function startNodeGame(node) {
  if (!isNodeBuilt(node)) return false;
  errorText.value = "";
  statusText.value = "";
  selectedLevel.value = null;
  await router.push({
    name: "game",
    params: { level: node.level },
    query: {
      entry: "custom-star",
      map: "custom-star",
      mapId: mapId.value,
      node: node.level,
      ...(shareCode.value ? { shareCode: shareCode.value } : {}),
      ...(publicAccess.value ? { access: "public" } : {})
    }
  });
  return true;
}

async function clickNode(node) {
  if (playMode.value) {
    if (!isNodeBuilt(node)) {
      statusText.value = languageStore.t("unavailable");
      errorText.value = "";
      return;
    }
    await startNodeGame(node);
    return;
  }

  if (!isOwner.value) return;

  if (!isNodeBuilt(node)) {
    router.push({
      name: "custom-level-editor",
      params: { mapId: mapId.value, level: node.level },
      query: { from: "custom-map-view" }
    });
    return;
  }
  selectedLevel.value = node.level;
}

function remakeSelected() {
  if (!selectedNode.value || !isOwner.value) return;
  router.push({
    name: "custom-level-editor",
    params: { mapId: mapId.value, level: selectedNode.value.level },
    query: { from: "custom-map-view" }
  });
}

async function playSelected() {
  await startNodeGame(selectedNode.value);
}

async function shareMap() {
  if (!map.value || !isOwner.value) return;
  statusText.value = "";
  errorText.value = "";
  if (map.value.visibility === "private") {
    errorText.value = languageStore.t("shareUnavailable");
    return;
  }
  try {
    let target = map.value;
    if (starMapStore.backendEnabled) {
      await starMapStore.createRemoteShareLink(mapId.value);
      target = starMapStore.getMap(mapId.value);
    }
    const result = await shareCustomMap(target);
    if (result.mode === "cancelled") return;
    statusText.value = languageStore.t("shareReady");
  } catch (error) {
    errorText.value = error?.message || languageStore.t("shareFailed");
  }
}

async function loadMapAuthority() {
  if (!starMapStore.backendEnabled) {
    starMapStore.refresh();
    return starMapStore.getMap(mapId.value);
  }

  if (shareCode.value) {
    return starMapStore.loadSharedRemoteMap(shareCode.value);
  }
  if (publicAccess.value) {
    return starMapStore.loadPublicRemoteMap(mapId.value);
  }
  return starMapStore.loadRemoteMap(mapId.value);
}

onMounted(async () => {
  loading.value = true;
  try {
    const loaded = await loadMapAuthority();
    if (!loaded) {
      await router.replace({ name: "workshop" });
      return;
    }

    if (String(loaded.id) !== mapId.value) {
      await router.replace({
        name: "custom-map-view",
        params: { mapId: loaded.id },
        query: { ...route.query }
      });
      return;
    }

    starMapStore.setActiveMap(mapId.value);

    if (starMapStore.backendEnabled && !shareCode.value) {
      try {
        await starMapStore.loadRemoteProgress(mapId.value);
      } catch {
        // Public/owner map may have no prior progress yet.
      }
    }
  } catch (error) {
    errorText.value = error?.message || languageStore.t("unavailable");
  } finally {
    loading.value = false;
  }
});
</script>

<template>
  <section v-if="map" class="custom-map page-card">
    <div class="page-header custom-header">
      <button class="back-button" type="button" @click="goParent">← {{ languageStore.t("back") }}</button>
      <div class="map-title desktop-map-title">
        <h1 class="page-title">{{ displayMapName }}</h1>

        <span>{{ languageStore.t("builtLevels", { done: builtCount, total: nodes.length }) }} · {{ languageStore.t("litLevels", { done: litCount, total: nodes.length }) }}</span>
      </div>
      <span class="header-spacer" />
    </div>

    <div class="mobile-map-summary">
      <h1 class="mobile-map-name" :title="displayMapName">{{ displayMapName }}</h1>
      <div class="mobile-map-meta">
        <strong>{{ visibilityLabel }}</strong>
        <span>{{ languageStore.t("builtLevels", { done: builtCount, total: nodes.length }) }} · {{ languageStore.t("litLevels", { done: litCount, total: nodes.length }) }}</span>
      </div>
      <div v-if="!playMode && isOwner" class="toolbar-actions mobile-toolbar-actions">
        <button class="mini-action" type="button" @click="router.push({ name: 'star-map-editor', query: { mapId, from: 'custom-map-view', parentMapId: mapId } })">{{ languageStore.t("starMapEdit") }}</button>
        <button class="mini-action" type="button" @click="shareMap">{{ languageStore.t("shareAction") }}</button>
        <button class="mini-action" type="button" @click="router.push({ name: 'map-manager', query: { from: 'custom-map-view', mapId } })">{{ languageStore.t("myCustomMaps") }}</button>
      </div>

    </div>

    <div v-if="!playMode && isOwner" class="map-toolbar desktop-map-toolbar">
      <div class="permission-badge">
        <span>{{ languageStore.t("mapPermission") }}</span>
        <strong>{{ visibilityLabel }}</strong>
      </div>
      <div class="toolbar-actions">
        <button class="mini-action" type="button" @click="router.push({ name: 'star-map-editor', query: { mapId, from: 'custom-map-view', parentMapId: mapId } })">{{ languageStore.t("starMapEdit") }}</button>
        <button class="mini-action" type="button" @click="shareMap">{{ languageStore.t("shareAction") }}</button>
        <button class="mini-action" type="button" @click="router.push({ name: 'map-manager', query: { from: 'custom-map-view', mapId } })">{{ languageStore.t("myCustomMaps") }}</button>
      </div>
    </div>

    <div class="drone-stage" :style="stageStyle">
      <img class="map-background-image" :src="map.imageDataUrl" :alt="displayMapName" draggable="false" />
      <button
        v-for="node in nodes"
        :key="node.id"
        type="button"
        class="drone-node"
        :class="[nodeState(node), { 'play-mode': playMode, disabled: playMode && !isNodeBuilt(node) }]"
        :aria-disabled="playMode && !isNodeBuilt(node) ? 'true' : 'false'"
        :style="{
          left: `${node.x * 100}%`,
          top: `${node.y * 100}%`,
          '--node-color': node.color || '#7ff8ef'
        }"
        @click="clickNode(node)"
      >
        <span class="drone-light" />
        <strong>{{ node.level }}</strong>
      </button>
      <div v-if="allLit" class="all-lit-label">{{ languageStore.t("mapFullyLit") }}</div>
    </div>

    <p v-if="statusText" class="status-text">{{ statusText }}</p>
    <p v-if="errorText" class="error-text">{{ errorText }}</p>

    <div v-if="selectedNode && !playMode && isOwner" class="node-action-mask" @click.self="selectedLevel = null">
      <div class="node-action-card">
        <div class="node-action-title">
          <span>{{ languageStore.t("level") }} {{ selectedNode.level }}</span>
          <small>{{ languageStore.t("levelBuilt") }}</small>
        </div>
        <button class="neon-button secondary" type="button" @click="remakeSelected">{{ languageStore.t("starMapEdit") }}</button>
        <button class="neon-button" type="button" @click="playSelected">{{ languageStore.t("play") }}</button>
        <button class="cancel-action" type="button" @click="selectedLevel = null">{{ languageStore.t("back") }}</button>
      </div>
    </div>
  </section>
</template>

<style scoped>
.custom-map { padding: 22px; min-height: min(820px, calc(100dvh - 48px)); }
.custom-header { align-items: center; }
.map-title { display: grid; justify-items: center; gap: 3px; text-align: center; }
.map-title .page-title { font-size: clamp(22px, 4vw, 34px); }
.map-title span { color: rgba(255,255,255,.5); font-size: 11px; }
.map-full-description { max-width:620px; margin:0; color:rgba(236,255,253,.66); font-size:11px; line-height:1.5; text-align:center; overflow-wrap:anywhere; }
.header-spacer { width: 54px; }
.mobile-map-summary { display:none; }
.map-toolbar { width: min(100%, 760px); margin: 0 auto 10px; display: flex; align-items: center; justify-content: space-between; gap: 12px; }
.permission-badge { display: flex; align-items: center; gap: 7px; color: rgba(255,255,255,.48); font-size: 11px; }
.permission-badge strong { color: #dffffb; }
.toolbar-actions { display: flex; gap: 7px; flex-wrap: wrap; justify-content: flex-end; }
.mini-action { border: 1px solid rgba(82,246,232,.28); border-radius: 999px; padding: 7px 10px; color: #dffffb; background: rgba(5,31,47,.8); font-size: 10px; font-weight: 800; }
.drone-stage { position: relative; width: min(100%,760px); max-height: 67dvh; margin: 0 auto; overflow: hidden; border: 1px solid rgba(82,246,232,.27); border-radius: 20px; background:#010711; isolation:isolate; }
.map-background-image { position:absolute; inset:0; z-index:0; width:100%; height:100%; object-fit:fill; opacity:1; filter:none; pointer-events:none; user-select:none; }
.drone-node { position: absolute; z-index: 3; width: 34px; height: 34px; border: 0; padding: 0; transform: translate(-50%,-50%); background: transparent; color: #fff; display: grid; place-items: center; }
.drone-node.play-mode.built,.drone-node.play-mode.lit { cursor:pointer; }
.drone-node.play-mode.disabled { cursor:not-allowed; opacity:.68; }
.drone-node.play-mode.built:hover .drone-light,.drone-node.play-mode.built:focus-visible .drone-light { transform:scale(1.28); border-color:#fff; }
.drone-node.play-mode.lit:hover .drone-light,.drone-node.play-mode.lit:focus-visible .drone-light { transform:scale(1.1); }
.drone-light { width:9px; height:9px; border-radius:50%; background:rgba(4,11,19,.72); border:1px solid rgba(255,255,255,.88); box-shadow:none; transition:.22s ease; position:relative; }
.drone-node strong { position:absolute; left:calc(50% + 8px); top:calc(50% + 5px); color:#fff; font-size:8px; font-weight:900; text-shadow:0 1px 5px #000,0 0 3px #000; }
.drone-node.built .drone-light { background:rgba(5,25,31,.82); border-color:rgba(198,255,250,.95); box-shadow:none; }
.drone-node.built strong { color:#fff; }
.drone-node.lit .drone-light { width:26px; height:26px; border:0; border-radius:0; background:transparent; box-shadow:none; }
.drone-node.lit .drone-light::before { content:'✦'; position:absolute; inset:0; display:grid; place-items:center; color:var(--node-color); font-size:24px; line-height:1; text-shadow:0 0 5px #fff,0 0 10px var(--node-color),0 0 20px var(--node-color); animation:nodeStarTwinkle 1.25s ease-in-out infinite; }
.drone-node.lit .drone-light::after { content:'✧'; position:absolute; right:-6px; top:-7px; color:#fff; font-size:11px; text-shadow:0 0 7px #fff; animation:nodeSparkTwinkle .85s ease-in-out infinite alternate; }
.drone-node.lit strong { color:#fff; }
@keyframes nodeStarTwinkle { 0%,100%{transform:scale(.82) rotate(-4deg);opacity:.76} 50%{transform:scale(1.2) rotate(4deg);opacity:1} }
@keyframes nodeSparkTwinkle { from{transform:scale(.55);opacity:.2} to{transform:scale(1.3);opacity:1} }
.all-lit-label { position: absolute; z-index: 2; left: 50%; bottom: 13px; transform: translateX(-50%); color: rgba(210,255,250,.72); font-size: 10px; letter-spacing: .18em; font-weight: 900; }
.legend { width: min(100%,760px); margin: 10px auto 0; display: flex; flex-wrap: wrap; gap: 12px; color: rgba(255,255,255,.48); font-size: 10px; }
.legend span { display: flex; align-items: center; gap: 5px; }
.legend-dot { width: 7px; height: 7px; border-radius: 50%; display: inline-block; background: #617582; }
.legend-dot.built { background:rgba(5,25,31,.82); border:1px solid rgba(198,255,250,.95); }
.legend-dot.lit { background:#fff; box-shadow:0 0 7px #7ff8ef; }
.status-text { width: min(100%,760px); margin: 10px auto 0; color: #99fff6; font-size: 11px; }
.error-text { width: min(100%,760px); margin: 10px auto 0; color: #ff9aa7; font-size: 11px; }
.node-action-mask { position: fixed; inset: 0; z-index: 5000; display: grid; place-items: center; padding: 18px; background: rgba(0,6,14,.72); backdrop-filter: blur(6px); }
.node-action-card { width: min(92vw, 390px); padding: 18px; border: 1px solid rgba(82,246,232,.45); border-radius: 18px; background: #061725; display: grid; gap: 9px; box-shadow: 0 24px 70px rgba(0,0,0,.55); }
.node-action-title { display: grid; gap: 4px; margin-bottom: 3px; }
.node-action-title span { font-size: 18px; font-weight: 900; }
.node-action-title small { color: rgba(255,255,255,.52); }
.cancel-action { border: 0; background: transparent; color: rgba(255,255,255,.55); padding: 8px; }
@media (max-width:560px) {
  .custom-map {
    padding: 12px;
    min-height: calc(100dvh - 24px);
    overflow-x: hidden;
  }

  .custom-header {
    display: block;
    margin-bottom: 9px;
  }

  .custom-header .back-button {
    display: inline-flex;
    width: auto;
    max-width: 100%;
    margin: 0;
    font-size: 18px;
    line-height: 1.2;
    white-space: nowrap;
  }

  .desktop-map-title,
  .header-spacer,
  .desktop-map-toolbar {
    display: none !important;
  }

  .mobile-map-summary {
    width: 100%;
    display: grid;
    gap: 9px;
    margin: 0 0 12px;
    padding: 12px;
    border: 1px solid rgba(82,246,232,.24);
    border-radius: 16px;
    background: rgba(4,25,39,.56);
  }

  .mobile-map-name {
    min-width: 0;
    margin: 0;
    color: #fff;
    font-size: 22px;
    line-height: 1.2;
    font-weight: 900;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  .mobile-map-description {
    margin: -1px 0 0;
    color: rgba(236,255,253,.68);
    font-size: 13px;
    line-height: 1.5;
    overflow-wrap: anywhere;
    white-space: normal;
  }

  .mobile-map-meta {
    min-width: 0;
    display: flex;
    align-items: center;
    gap: 8px;
    color: rgba(255,255,255,.58);
    font-size: 12px;
    line-height: 1.35;
    white-space: nowrap;
    overflow: hidden;
  }

  .mobile-map-meta strong {
    flex: 0 0 auto;
    color: #dffffb;
  }

  .mobile-map-meta span {
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  .mobile-toolbar-actions {
    width: 100%;
    display: grid;
    grid-template-columns: repeat(3, minmax(0, 1fr));
    gap: 7px;
  }

  .mobile-toolbar-actions .mini-action {
    min-width: 0;
    width: 100%;
    min-height: 39px;
    padding: 8px 4px;
    font-size: 12px;
    white-space: nowrap;
  }

  .mobile-play-hint {
    color: rgba(255,255,255,.55);
    font-size: 12px;
  }

  .drone-stage {
    width: 100%;
    max-height: none;
    margin-top: 0;
    border-radius: 15px;
  }

  .map-background-image { object-fit: fill; }
  .drone-node { width: 29px; height: 29px; }
  .drone-light { width: 8px; height: 8px; }
  .drone-node.lit .drone-light { width:24px; height:24px; }
  .drone-node strong { font-size: 7px; }
  .legend {
    gap: 9px;
    margin-top: 8px;
    font-size: 10px;
    line-height: 1.25;
  }
}

/* REMOVE_RED_FRAME_SUBTITLES_V1_START */
.tile-subtitle,
.nav-subtitle,
.feature-subtitle,
.card-subtitle,
.workshop-subtitle,
.map-subtitle,
.empty-description,
.empty-hint,
.helper-text,
.form-hint,
.status-hint {
  display: none !important;
}
/* REMOVE_RED_FRAME_SUBTITLES_V1_END */


/* REMOVE_RED_FRAME_REMAINS_V2_START */
.tile-subtitle,
.nav-subtitle,
.feature-subtitle,
.card-subtitle,
.map-subtitle,
.workshop-subtitle,
.empty-description,
.empty-hint,
.helper-text,
.form-hint,
.status-hint,
.description,
.description-text,
.description-label,
.map-description,
.map-description-label,
.map-description-text,
.generated-hint,
.generated-status,
.generation-hint,
.generation-status,
.special-level-card,
.special-level,
.special-chapter,
.chapter-endpoint {
  display: none !important;
}
/* REMOVE_RED_FRAME_REMAINS_V2_END */

</style>
